import React from 'react';
import styled from '@emotion/styled';
import { useTrackEvent } from 'tracking/useTrackEvent';
import { Button, Colors, Typography } from '@zapier/design-system';
import { mediaQueries } from 'components/mediaQueries';
import { GeneralCTAInfo } from 'rest/contentful/types';
import { OptionSelected, TrackedComponent, useAvroTracking } from 'tracking';

const primaryButton = {
  text: 'Sign up',
};

const secondaryButton = {
  text: 'Learn more',
  href: '/how-it-works',
};

export const SignupPopup = ({ ctaInfo }: { ctaInfo: GeneralCTAInfo }) => {
  const trackEvent = useTrackEvent();
  const { emitCTAClickEvent } = useAvroTracking();

  const handlePrimaryClick = () => {
    trackEvent('call_to_action', {
      path: window.location.pathname,
      from_path: document.referrer,
      cta_name: ctaInfo.name,
      cta_raw_text: ctaInfo.rawText,
      cta_location: ctaInfo.location,
      cta_goal: ctaInfo.goal,
      signup_type: '',
      link_destination: ctaInfo.linkDestination,
    });

    emitCTAClickEvent({
      destination_url_address: ctaInfo.linkDestination,
      interaction_raw_text: primaryButton.text,
      custom_tracking_fields: {
        component: TrackedComponent.SignupPopup,
      },
    });
  };

  const handleSecondaryClick = () => {
    emitCTAClickEvent({
      destination_url_address: secondaryButton.href,
      interaction_raw_text: secondaryButton.text,
      option_selected: OptionSelected.secondary_cta_button,
      custom_tracking_fields: {
        component: TrackedComponent.SignupPopup,
      },
    });
  };

  return (
    <Outer data-testid="signup-popup">
      <Heading>{ctaInfo.headline}</Heading>
      <div>
        <Details>{ctaInfo.detailsText}</Details>
        <Actions>
          <ButtonWrapper>
            <Button
              href={ctaInfo.linkDestination}
              hasShadow={true}
              isFullWidth={true}
              size="small"
              onClick={handlePrimaryClick}
            >
              {primaryButton.text}
            </Button>
          </ButtonWrapper>
          <Button
            size="small"
            color="secondary"
            href={secondaryButton.href}
            onClick={handleSecondaryClick}
          >
            {secondaryButton.text}
          </Button>
        </Actions>
      </div>
    </Outer>
  );
};

const Outer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  margin-left: 1px;

  p {
    margin-bottom: 10px;
  }

  ${mediaQueries.tablet} {
    align-items: center;
    flex-direction: row;
    p {
      margin-bottom: 0;
    }

    > p {
      margin-right: 7%;
    }
  }

  ${mediaQueries.desktop} {
    align-items: flex-start;
    flex-direction: column;

    p {
      margin-bottom: 10px;
    }
  }
`;

const Heading = styled.p`
  color: ${Colors.neutral800};
  ${Typography.paragraph1ShortBold}
`;

const Details = styled.p`
  display: none;
  ${mediaQueries.desktop} {
    color: #999;
    display: block;
    margin-bottom: 10px;
    ${Typography.smallPrint1}
  }
`;

const Actions = styled.div`
  display: grid;
  grid-template-areas: 'button link';
  grid-template-columns: repeat(2, max-content);
  grid-column-gap: 15px;
  place-items: center;
  ${mediaQueries.tablet} {
    grid-template-areas: 'link button';
  }
  ${mediaQueries.desktop} {
    grid-template-areas: 'button link';
  }
`;

const ButtonWrapper = styled.div`
  grid-area: button;
`;
